import * as signalR from '@microsoft/signalr';
import axios from '../../store/sagas/axiosConfig';
import { TEMP_NUMBER } from './constants/nlpSettings';

/**
 * Send a conversation history message to the backend endpoint which connected to
 * a chat bot API and return the response in all in a bulk message.
 */
async function askChatGPT({
  messages,
  nlpConstants,
}) {
  const WAIT_NLP_RESPONSE_TIMEOUT_LIMIT = nlpConstants
    ? parseInt(nlpConstants.WAIT_NLP_RESPONSE_TIMEOUT_LIMIT.value, 10)
    : TEMP_NUMBER;

  let PREFER_MODEL = null;
  if (nlpConstants && nlpConstants.PREFER_MODEL.value.trim().length > 0) {
    PREFER_MODEL = nlpConstants.PREFER_MODEL.value;
  }
  let ALT_MODEL = null;
  if (nlpConstants && nlpConstants.ALT_MODEL.value.trim().length > 0) {
    ALT_MODEL = nlpConstants.ALT_MODEL.value;
  }
  let response;
  try {
    // Create URL paramter string for model selection
    let modelSelectionParam = '';
    if (PREFER_MODEL) {
      modelSelectionParam += `?preferModel=${PREFER_MODEL}`;
    }
    if (ALT_MODEL) {
      if (modelSelectionParam === '') {
        modelSelectionParam += `?altModel=${ALT_MODEL}`;
      } else {
        modelSelectionParam += `&altModel=${ALT_MODEL}`;
      }
    }

    // endpoint URL for getting bulk response message
    const url = `/nlp/ask${modelSelectionParam}`;

    // Config the axios request and send it
    const config = {
      method: 'post',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
      data: messages,
      timeout: WAIT_NLP_RESPONSE_TIMEOUT_LIMIT * 1000, // Must be in milliseconds
    };
    response = await axios(config);
  } catch (error) {
    if (error.response) {
      throw new Error(`Received error code ${error.response.status} from ChatGPT`);
    } else {
      throw error;
    }
  }

  if (!response || !response.data || response.data.length === 0) {
    throw new Error('Error: received an empty response from ChatGPT');
  }
  return response.data;
}

/**
 * Send a conversation history message to the backend endpoint which connected to
 * a chat bot API and return the response in all in a streamed word-by-word message.
 */
async function askChatGPTStream({
  messages,
  nlpConstants,
  onUpdateMessage,
  resetEmailMessage,
  instructionName,
  travelAgencyId,
}) {
  const WAIT_NLP_RESPONSE_TIMEOUT_LIMIT = nlpConstants
    ? parseInt(nlpConstants.WAIT_NLP_RESPONSE_TIMEOUT_LIMIT.value, 10)
    : TEMP_NUMBER;

  let PREFER_MODEL = null;
  if (nlpConstants && nlpConstants.PREFER_MODEL.value.trim().length > 0) {
    PREFER_MODEL = nlpConstants.PREFER_MODEL.value;
  }
  let ALT_MODEL = null;
  if (nlpConstants && nlpConstants.ALT_MODEL.value.trim().length > 0) {
    ALT_MODEL = nlpConstants.ALT_MODEL.value;
  }
  const apologyKeywords = ['I\'m sorry', 'I’m sorry', 'Unfortunately', 'I apologize', 'I regret that'];

  const transport = signalR.HttpTransportType.WebSockets;

  const options = {
    transport,
    skipNegotiation: true,
  };

  // Create a connection to the signalR hub to receive streamed messages
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(`${axios.defaults.baseURL}/hubs/nlp`, options) // Connect to signalR Hub for streamed NLP response
    .build();
  await connection.start();

  let hasReceivedAnyChunks = false;
  let isCanceled = false;
  let finalMessage = '';

  /* Wrap the setTimeout call in a Promise, so it can throw an error back to caller level,
   * if enough time passes without a single chunk being received */
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(
      () => {
        if (!hasReceivedAnyChunks) {
          isCanceled = true;
          connection.stop();
          reject(new Error('ChatGPT took too long to respond')); // Reject the Promise instead of throwing
        }
      },
      WAIT_NLP_RESPONSE_TIMEOUT_LIMIT * 1000
      * (resetEmailMessage ? 3 : 1), // Must be in milliseconds
    );
  });

  // Listen to the streaming responses and the timeout concurrently
  await Promise.race([
    new Promise((resolve, reject) => {
      connection.stream('AskStreamed', JSON.stringify(messages), PREFER_MODEL, ALT_MODEL, instructionName, travelAgencyId)
        .subscribe({
          next: (item) => {
            if (!isCanceled) {
              hasReceivedAnyChunks = true;
              finalMessage += item;
              // Call in the next event loop cycle to give browser a chance to update the UI.
              setTimeout(() => onUpdateMessage(finalMessage), 0);
            }
          },
          complete: () => {
            if (!isCanceled) {
              resolve(finalMessage);
              if (resetEmailMessage && finalMessage
                && !apologyKeywords.some((keyword) => finalMessage.startsWith(keyword))) {
                resetEmailMessage(finalMessage);
              }
            }
          },
          error: (error) => {
            if (!isCanceled) {
              reject(new Error(`ChatGPT streaming failed due to ${error}`));
            }
          },
        });

      // Handle Hub suddenly close error
      connection.onclose((error) => {
        if (!isCanceled) {
          reject(new Error(`ChatGPT streaming closed due to ${error}`));
        }
      });
    }),
    timeoutPromise,
  ]);
}

export { askChatGPT, askChatGPTStream };
